/**
 * OperationalStepsDialog.tsx
 * PR B (qa/test-catalog-modernize-2026-07-11/PLAN.md) — B4: compact dialog for
 * the OPERATIONAL task types (active task types in no recipe — the rock prep
 * steps: Cutting / Grinding / Conformity, auto-inserted via RockPrepStep and
 * never quoted, D3). Kept out of the main test list.
 *
 * Edit reuses the shared TaskTypeEditForm + TaskTypeMachinesEditor (D5 — one
 * source of truth with the retired Task Types dialog).
 */

import { useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, IconButton, List, ListItem, ListItemText, Tooltip, Typography,
  useTheme,
} from '@mui/material';
import { Close, Edit, HardwareOutlined, PrecisionManufacturing, WarningAmber } from '@mui/icons-material';
import type { TestDefinitionTaskType } from '../../../services/testDefinitionService';
import { updateTaskType } from '../../../services/scheduleTaxonomy';
import type { Skill } from '../../../types/schedule';
import { TaskTypeMachinesEditor } from './TaskTypeMachinesEditor';
import {
  TaskTypeEditForm,
  EMPTY_TASK_TYPE_FORM,
  validateTaskTypeForm,
  payloadFromTaskTypeForm,
  type TaskTypeFormState,
} from './TaskTypeEditForm';

interface Props {
  open: boolean;
  onClose: () => void;
  steps: TestDefinitionTaskType[];
  skills: Skill[];
  /** Refreshes the unified payload after an edit. */
  onChanged: () => void;
}

/** TaskTypeEditForm seed from the unified payload's task-type shape. */
function formFromStep(tt: TestDefinitionTaskType): TaskTypeFormState {
  return {
    name: tt.Name,
    category: tt.Category ?? '',
    subcategory: tt.Subcategory ?? '',
    labSection: tt.LabSection ?? '',
    presetDurationDays: String(tt.PresetDurationDays ?? 1),
    defaultMachineCount: String(tt.DefaultMachineCount ?? 1),
    sortOrder: String(tt.SortOrder ?? 0),
    skillKey: tt.SkillKey ?? '',
    runMode: tt.RunMode ?? 'attended',
    attendedMinutes: tt.AttendedMinutes != null ? String(tt.AttendedMinutes) : '',
    defaultMinutes: tt.DefaultMinutes != null ? String(tt.DefaultMinutes) : '',
  };
}

export const OperationalStepsDialog = ({ open, onClose, steps, skills, onChanged }: Props) => {
  const theme = useTheme();
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [form, setForm] = useState<TaskTypeFormState>(EMPTY_TASK_TYPE_FORM);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [machinesTarget, setMachinesTarget] = useState<{
    key: string; name: string; runMode: 'attended' | 'continuous';
  } | null>(null);

  const handleStartEdit = (tt: TestDefinitionTaskType) => {
    setEditingKey(tt.TaskTypeKey);
    setForm(formFromStep(tt));
    setErrorMsg(null);
  };

  const handleSave = async () => {
    const validationError = validateTaskTypeForm(form);
    if (validationError) {
      setErrorMsg(validationError);
      return;
    }
    setSaving(true);
    setErrorMsg(null);
    try {
      await updateTaskType(editingKey as string, payloadFromTaskTypeForm(form));
      setEditingKey(null);
      setForm(EMPTY_TASK_TYPE_FORM);
      onChanged();
    } catch (err) {
      setErrorMsg(
        (err as { response?: { data?: { error?: string } } }).response?.data?.error
        ?? (err instanceof Error ? err.message : 'Failed to save'),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PrecisionManufacturing />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Operational Steps</Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><Close /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Auto-inserted work steps — not quotable. These task types belong to no test's
            recipe; the scheduler inserts them itself (e.g. rock prep: cutting, grinding,
            conformity checks).
          </Typography>
        </Box>

        {errorMsg && (
          <Alert severity="error" sx={{ mx: 2, mb: 1 }} onClose={() => setErrorMsg(null)}>
            {errorMsg}
          </Alert>
        )}

        <List dense disablePadding>
          {steps.length === 0 && (
            <ListItem>
              <ListItemText
                primary={(
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No operational steps — every active task type belongs to a test's recipe.
                  </Typography>
                )}
              />
            </ListItem>
          )}
          {steps.map((tt) => (
            <Box key={tt.TaskTypeKey}>
              <Divider />
              {editingKey === tt.TaskTypeKey ? (
                <TaskTypeEditForm
                  mode={tt.TaskTypeKey}
                  form={form}
                  setForm={setForm}
                  skills={skills}
                  saving={saving}
                  onSave={handleSave}
                  onCancel={() => { setEditingKey(null); setErrorMsg(null); }}
                />
              ) : (
                <ListItem
                  secondaryAction={(
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() => setMachinesTarget({
                          key: tt.TaskTypeKey,
                          name: tt.Name,
                          runMode: tt.RunMode ?? 'attended',
                        })}
                        title="Edit machines and machine time"
                        disabled={saving}
                      >
                        <HardwareOutlined fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleStartEdit(tt)}
                        title="Edit"
                        disabled={saving}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                >
                  <ListItemText
                    primary={(
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{tt.Name}</Typography>
                        {tt.LabSection && (
                          <Chip label={tt.LabSection} size="small" variant="outlined" sx={{ fontSize: 10 }} />
                        )}
                        {tt.CapabilityCount === 0 && (
                          <Tooltip title="No machine mapped yet — click the machine icon to assign one.">
                            <Chip
                              icon={<WarningAmber sx={{ fontSize: '0.85rem !important', color: theme.schedule.maintenance }} />}
                              label="Needs machine/time"
                              size="small"
                              sx={{
                                fontSize: 10,
                                bgcolor: `${theme.schedule.maintenance}1A`,
                                borderColor: theme.schedule.maintenance,
                                color: theme.schedule.maintenance,
                                fontWeight: 600,
                                border: '1px solid',
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    )}
                    secondary={(
                      <Typography variant="caption" color="text.secondary">
                        {tt.PresetDurationDays} day{tt.PresetDurationDays !== 1 ? 's' : ''} preset
                        {tt.DefaultMinutes != null ? ` · ${tt.DefaultMinutes} min machine time` : ''}
                        {tt.SkillKey
                          ? ` · requires ${skills.find((s) => s.SkillKey === tt.SkillKey)?.Name ?? 'skill'}`
                          : ' · no skill required'}
                      </Typography>
                    )}
                  />
                </ListItem>
              )}
            </Box>
          ))}
        </List>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">Close</Button>
      </DialogActions>

      {machinesTarget && (
        <TaskTypeMachinesEditor
          open={!!machinesTarget}
          taskTypeKey={machinesTarget.key}
          taskTypeName={machinesTarget.name}
          runMode={machinesTarget.runMode}
          onClose={() => setMachinesTarget(null)}
          onSaved={() => {
            setMachinesTarget(null);
            onChanged();
          }}
        />
      )}
    </Dialog>
  );
};
